// The bankroll, layered over time: what is not in play at the bottom, each live call stacked on
// top (or all of them as one), the bankroll along the top edge. Each layer's tag on the right says
// what it is worth now; a tap turns a call's tag into its cash out, a second tap sends it.

import { useEffect, useRef, useState } from "react";
import { big, pnl, sign, usd } from "../../lib/num";
import { useAccount } from "../../state/account";
import { useStore } from "../../state/store";
import { valuations as valuationStore } from "../../state/valuation";
import { dollarsOf, useBankrollSeries, type BankrollSeries } from "./bankroll-series";

export type Mode = "split" | "total";

type Palette = { up: string; down: string; ink: string; muted: string; faint: string; bg: string; accent: string };

function palette(): Palette {
  const css = getComputedStyle(document.documentElement);
  const read = (name: string) => css.getPropertyValue(name).trim();
  return { up: read("--c-up"), down: read("--c-down"), ink: read("--c-ink"), muted: read("--c-muted"), faint: read("--c-faint"), bg: read("--c-surface"), accent: read("--c-accent") };
}

function alpha(hex: string, a: number): string {
  const clean = hex.replace("#", "");
  const value = Number.parseInt(clean.length === 3 ? clean.replace(/./g, "$&$&") : clean, 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${a})`;
}

/** Each layer's strength, alternating so neighbours of one tone stay apart. */
export const layerAlpha = (index: number) => (index % 2 === 0 ? 0.5 : 0.3);

type Drawn = { key: string; ids: string[]; tone: "up" | "down" | "flat"; values: number[]; now: number };

const TAG_H = 30;
const TAG_GAP = 4;

function hhmm(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

export function BankrollChart({ mode, onCashOut, closing }: { mode: Mode; onCashOut: (ids: string[]) => void; closing: Set<string> }) {
  const series = useBankrollSeries();
  const account = useAccount();
  const valued = useStore(valuationStore);
  const host = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const seriesRef = useRef<BankrollSeries | null>(null);
  const modeRef = useRef<Mode>(mode);
  const hover = useRef<number | null>(null);
  const layout = useRef<Record<string, number>>({});
  const scale = useRef({ max: 0, ready: false });
  const [tags, setTags] = useState<Record<string, number>>({});
  const [confirming, setConfirming] = useState<string | null>(null);

  seriesRef.current = series;
  modeRef.current = mode;

  // a tap asks, a second tap within four seconds sends
  useEffect(() => {
    if (!confirming) return;
    const timer = setTimeout(() => setConfirming(null), 4_000);
    return () => clearTimeout(timer);
  }, [confirming]);

  // the tags follow the layers a few times a second
  useEffect(() => {
    const timer = setInterval(() => {
      setTags((held) => {
        const next = layout.current;
        const same = Object.keys(next).length === Object.keys(held).length && Object.entries(next).every(([key, y]) => Math.abs((held[key] ?? -1) - y) < 0.5);
        return same ? held : { ...next };
      });
    }, 150);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const box = host.current;
    const el = canvas.current;
    if (!box || !el) return;
    const fit = () => {
      const ratio = Math.min(2, window.devicePixelRatio || 1);
      const { width, height } = box.getBoundingClientRect();
      el.width = Math.round(width * ratio);
      el.height = Math.round(height * ratio);
      el.style.width = `${width}px`;
      el.style.height = `${height}px`;
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const ctx = el.getContext("2d");
    if (!ctx) return;
    const c = palette();
    let frame = 0;
    let last = 0;

    const draw = (time: number) => {
      frame = requestAnimationFrame(draw);
      if (time - last < 32 || document.visibilityState !== "visible") return;
      last = time;
      const s = seriesRef.current;
      const live = valuationStore.get();
      const ratio = el.width / Math.max(1, el.clientWidth);
      const width = el.clientWidth;
      const height = el.clientHeight;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.clearRect(0, 0, width, height);
      if (!s || !live || width === 0 || height === 0) return;

      const now = Date.now();
      const gutter = width < 560 ? 104 : 128;
      const plotW = width - gutter;
      // room above the bankroll's edge for the each-call or together switch
      const padTop = 40;
      const padBottom = 20;
      const plotH = height - padTop - padBottom;
      const start = now - s.span;
      const x = (t: number) => ((t - start) / (now - start)) * plotW;

      // now, from the live valuation: exact payouts, and what is not in play
      const payouts = new Map(live.positions.map((valuation) => [valuation.positionId, valuation]));
      const inPlayNow = live.positions.reduce((sum, valuation) => sum + dollarsOf(valuation.payout), 0);
      const idleNow = Math.max(0, dollarsOf(live.equity) - inPlayNow);

      const keep = s.times.map((t) => t >= start - s.span / s.times.length);
      const times = [...s.times.filter((_, k) => keep[k]), now];
      const pick = (values: number[], nowValue: number) => [...values.filter((_, k) => keep[k]), nowValue];
      const idle = pick(
        s.idle.map((value) => value + idleNow),
        idleNow,
      );

      let drawn: Drawn[];
      if (modeRef.current === "total") {
        const sums = s.times.map((_, k) => s.layers.reduce((sum, layer) => sum + (layer.values[k] ?? 0), 0));
        const net = big(live.openPnl);
        drawn = [{ key: "all", ids: s.layers.flatMap((layer) => layer.ids), tone: net > 0n ? "up" : net < 0n ? "down" : "flat", values: pick(sums, inPlayNow), now: inPlayNow }];
      } else {
        drawn = s.layers.map((layer) => {
          const valuation = payouts.get(layer.key);
          const nowValue = valuation ? dollarsOf(valuation.payout) : (layer.values.at(-1) ?? 0);
          const net = valuation ? sign(valuation.payoutPnl) : 0;
          return { key: layer.key, ids: layer.ids, tone: net > 0 ? "up" : net < 0 ? "down" : "flat", values: pick(layer.values, nowValue), now: nowValue };
        });
      }

      // the stacks, bottom up
      const stacks: number[][] = [idle];
      for (const layer of drawn) {
        const below = stacks.at(-1) as number[];
        stacks.push(layer.values.map((value, k) => (below[k] ?? 0) + value));
      }
      const top = stacks.at(-1) as number[];
      const highest = Math.max(...top, 1) * 1.12;
      const sc = scale.current;
      if (!sc.ready) {
        sc.max = highest;
        sc.ready = true;
      } else sc.max += (highest - sc.max) * 0.12;
      const y = (value: number) => padTop + (1 - value / sc.max) * plotH;

      const band = (lower: number[] | null, upper: number[], fill: string | CanvasGradient) => {
        ctx.beginPath();
        upper.forEach((value, k) => {
          const px = x(times[k] as number);
          if (k === 0) ctx.moveTo(px, y(value));
          else ctx.lineTo(px, y(value));
        });
        for (let k = upper.length - 1; k >= 0; k -= 1) ctx.lineTo(x(times[k] as number), lower ? y(lower[k] ?? 0) : y(0));
        ctx.closePath();
        ctx.fillStyle = fill;
        ctx.fill();
      };
      const edge = (values: number[], stroke: string, lineWidth: number) => {
        ctx.beginPath();
        values.forEach((value, k) => {
          const px = x(times[k] as number);
          if (k === 0) ctx.moveTo(px, y(value));
          else ctx.lineTo(px, y(value));
        });
        ctx.strokeStyle = stroke;
        ctx.lineWidth = lineWidth;
        ctx.lineJoin = "round";
        ctx.stroke();
      };

      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, plotW + 1, height);
      ctx.clip();

      // not in play: the calm base
      band(null, idle, alpha(c.ink, 0.07));
      edge(idle, alpha(c.ink, 0.28), 1);

      // each call (or all of them), coloured by how it stands now
      drawn.forEach((layer, index) => {
        const lower = stacks[index] as number[];
        const upper = stacks[index + 1] as number[];
        const tone = layer.tone === "up" ? c.up : layer.tone === "down" ? c.down : c.muted;
        const gradient = ctx.createLinearGradient(0, padTop, 0, padTop + plotH);
        gradient.addColorStop(0, alpha(tone, layerAlpha(index)));
        gradient.addColorStop(1, alpha(tone, layerAlpha(index) * 0.45));
        band(lower, upper, gradient);
        edge(upper, alpha(tone, 0.9), 1);
      });

      // the bankroll along the top
      ctx.shadowColor = alpha(c.ink, 0.35);
      ctx.shadowBlur = 8;
      edge(top, c.ink, 2);
      ctx.shadowBlur = 0;
      ctx.restore();

      const headY = y(top.at(-1) ?? 0);
      const pulse = (now % 1600) / 1600;
      ctx.beginPath();
      ctx.arc(plotW, headY, 4 + pulse * 10, 0, Math.PI * 2);
      ctx.fillStyle = alpha(c.ink, 0.22 * (1 - pulse));
      ctx.fill();
      ctx.beginPath();
      ctx.arc(plotW, headY, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = c.ink;
      ctx.fill();

      // where each tag sits: centred on its layer now, nudged apart, kept inside the box
      const wanted = [
        { key: "idle", y: (y(0) + y(idle.at(-1) ?? 0)) / 2 },
        ...drawn.map((layer, index) => ({ key: layer.key, y: (y((stacks[index] as number[]).at(-1) ?? 0) + y((stacks[index + 1] as number[]).at(-1) ?? 0)) / 2 })),
      ];
      const placed: Record<string, number> = {};
      let floor = padTop + plotH - TAG_H / 2;
      for (const tag of wanted) {
        const at = Math.min(tag.y, floor);
        placed[tag.key] = at;
        floor = at - TAG_H - TAG_GAP;
      }
      const highestTag = Math.min(...Object.values(placed));
      if (highestTag < TAG_H / 2) {
        const shift = TAG_H / 2 - highestTag;
        for (const key of Object.keys(placed)) placed[key] = (placed[key] as number) + shift;
      }
      layout.current = placed;

      // the crosshair: the bankroll then
      const pointer = hover.current;
      if (pointer !== null && pointer < plotW) {
        let nearest = 0;
        for (let k = 0; k < times.length; k += 1) if (Math.abs(x(times[k] as number) - pointer) < Math.abs(x(times[nearest] as number) - pointer)) nearest = k;
        const px = x(times[nearest] as number);
        ctx.strokeStyle = alpha(c.ink, 0.18);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(Math.round(px) + 0.5, padTop);
        ctx.lineTo(Math.round(px) + 0.5, padTop + plotH);
        ctx.stroke();
        const total = top[nearest] ?? 0;
        const inPlay = total - (idle[nearest] ?? 0);
        const words = `${hhmm(times[nearest] as number)}  $${total.toFixed(2)}  ·  $${inPlay.toFixed(2)} in play`;
        ctx.font = "500 11px 'Inter Tight', Inter, system-ui, sans-serif";
        ctx.textBaseline = "middle";
        const w = ctx.measureText(words).width + 16;
        const bx = Math.min(Math.max(0, px - w / 2), plotW - w);
        ctx.fillStyle = alpha(c.bg, 0.95);
        ctx.fillRect(bx, 0, w, 18);
        ctx.fillStyle = c.ink;
        ctx.textAlign = "left";
        ctx.fillText(words, bx + 8, 9);
      }

      // the window's start and now, quietly
      ctx.font = "500 10px 'Inter Tight', Inter, system-ui, sans-serif";
      ctx.fillStyle = c.faint;
      ctx.textBaseline = "alphabetic";
      ctx.textAlign = "left";
      ctx.fillText(hhmm(start), 0, height - 4);
      ctx.textAlign = "right";
      ctx.fillText("now", plotW, height - 4);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, []);

  // the tags' words, from the live valuation: exact
  const ready = series !== null && valued !== null && account !== null;
  const payouts = new Map((valued?.positions ?? []).map((valuation) => [valuation.positionId, valuation]));
  const inPlay = (valued?.positions ?? []).reduce((sum, valuation) => sum + big(valuation.payout), 0n);
  const idleRaw = valued ? big(valued.equity) - inPlay : 0n;
  const items = !ready
    ? []
    : mode === "total"
      ? [{ key: "all", ids: series.layers.flatMap((layer) => layer.ids), label: `${series.layers.length} live calls`, value: inPlay, net: big(valued.openPnl), index: 0 }]
      : series.layers.map((layer, index) => {
          const valuation = payouts.get(layer.key);
          return {
            key: layer.key,
            ids: layer.ids,
            label: `${layer.isLong ? "▲" : "▼"} ${layer.symbol}`,
            value: valuation ? big(valuation.payout) : 0n,
            net: valuation ? big(valuation.payoutPnl) : 0n,
            index,
          };
        });

  return (
    <div className={`bk ${ready ? "" : "bk-wait"}`} aria-busy={!ready || undefined}>
      <div
        className="bk-host"
        ref={host}
        onPointerMove={(event) => {
          hover.current = event.clientX - event.currentTarget.getBoundingClientRect().left;
        }}
        onPointerLeave={() => {
          hover.current = null;
        }}
      >
        <canvas ref={canvas} className="bk-canvas" role="img" aria-label="Your bankroll over time: not in play, and each live call on top" />
        <div className="bk-tags">
          {ready && (
            <span className="bk-tag bk-tag-idle" style={{ top: tags.idle ?? -999 }}>
              <span className="bk-tag-label">Not in play</span>
              <span className="bk-tag-value num">{usd(idleRaw > 0n ? idleRaw : 0n)}</span>
            </span>
          )}
          {items.map((item) => {
            const asking = confirming === item.key;
            const busy = item.ids.some((id) => closing.has(id));
            const tone = item.net > 0n ? "up" : item.net < 0n ? "down" : "flat";
            return (
              <button
                key={item.key}
                type="button"
                className={`bk-tag bk-tag-call bk-${tone} ${asking ? "is-asking" : ""}`}
                style={{ top: tags[item.key] ?? -999, ["--layer" as string]: String(item.index % 2) }}
                disabled={busy}
                title={`${item.label} · ${pnl(item.net)}`}
                onClick={() => {
                  if (asking) {
                    setConfirming(null);
                    onCashOut(item.ids);
                  } else setConfirming(item.key);
                }}
              >
                {asking ? (
                  <span className="bk-tag-ask">Cash out {usd(item.value)}</span>
                ) : (
                  <>
                    <span className="bk-tag-label">{busy ? "Cashing out" : item.label}</span>
                    <span className="bk-tag-value num">{usd(item.value)}</span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
